import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type PreguntasCargo = components['schemas']['PreguntasCargoRead']
export type PreguntaCargo = components['schemas']['PreguntaRead']
export type PreguntaRespuestaTipo = components['schemas']['PreguntaRespuestaTipo']
export type DecisionContradiccion = components['schemas']['DecisionContradiccion']

function base(businessId: string | undefined) {
  return `/businesses/${businessId}/sgsst/preguntas-cargo`
}

function claveDe(businessId: string | undefined, positionId: string | undefined) {
  return ['preguntas-cargo', businessId, positionId]
}

/** Las tres que Kairos pide llegan enteras: grupo fijo que R6 exceptúa. */
export function usePreguntasCargo(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: claveDe(businessId, positionId),
    queryFn: async () => (await api.get<PreguntasCargo>(base(businessId), {params: {position_id: positionId}})).data,
    enabled: !!businessId && !!positionId,
  })
}

export type EstadoPregunta = 'empresa' | 'pendiente' | 'descartada'

export const PREGUNTAS_PAGE_SIZE = 10

/** La clave cuelga de la del cargo: las mutaciones que invalidan esa refrescan también esta. */
export function usePreguntasLista(
  businessId: string | undefined,
  positionId: string | undefined,
  estado: EstadoPregunta,
  page: number,
  search: string
) {
  return usePagedList<PreguntaCargo>(
    [...claveDe(businessId, positionId), 'lista', estado],
    businessId && positionId ? `${base(businessId)}/lista` : undefined,
    {page, search, extra: {position_id: positionId, estado}},
    PREGUNTAS_PAGE_SIZE
  )
}

/** `position_id` va en `null` para una pregunta de empresa: es el mismo endpoint. */
export function useResponderPregunta(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      preguntaKey: string
      esDeEmpresa: boolean
      respuestaTipo: PreguntaRespuestaTipo
      respuesta: string | null
      motivo: string | null
    }) =>
      (
        await api.post<PreguntaCargo>(`${base(businessId)}/${input.preguntaKey}/responder`, {
          position_id: input.esDeEmpresa ? null : positionId,
          respuesta_tipo: input.respuestaTipo,
          respuesta: input.respuesta,
          motivo: input.motivo,
        })
      ).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: claveDe(businessId, positionId)})
      track('pregunta_cargo_respondida', {business_id: businessId, respuesta_tipo: variables.respuestaTipo})
    },
  })
}

export function useDecidirContradiccion(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      preguntaKey: string
      manda: DecisionContradiccion
      motivo: string
      decisionVista: DecisionContradiccion | null
    }) =>
      (
        await api.post<PreguntaCargo>(`${base(businessId)}/${input.preguntaKey}/contradiccion`, {
          position_id: positionId,
          manda: input.manda,
          motivo: input.motivo,
          // La decisión que quien firma tenía delante: sin ella dos firmantes en paralelo
          // se pisan y gana el último.
          decision_vista: input.decisionVista,
        })
      ).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: claveDe(businessId, positionId)})
      track('pregunta_cargo_contradiccion_decidida', {business_id: businessId, manda: variables.manda})
    },
  })
}
